import { Component, computed, inject, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatTab, MatTabGroup } from "@angular/material/tabs";
import Swal from 'sweetalert2';

import { CredentialService } from "@fry/system/account/credential/crendential.service";
import { CredentialTypes, MFAMethod } from '@fry-it/risr-core';
import {
  ApiCredential,
  LocalCredential,
  ProxyCredential,
  READABLE_CREDENTIAL_TYPES
} from "@fry/system/account/credential/credential";
import { AuthService } from '@fry-it/risr-core';
import {
  CreateLocalCredentialDialogComponent
} from "./dialogs/create-local-credential-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import {
  CreateProxyCredentialDialogComponent
} from "./dialogs/create-proxy-credential-dialog.component";
import {
  UpdateCredentialUsernameDialogComponent
} from "@fry/system/account/credential/dialogs/update-credential-username-dialog.component";
import {
  UpdatePasswordDialogComponent
} from "@fry/system/account/credential/dialogs/update-credential-password-dialog.component";
import { SecurityService } from '@fry-it/risr-core';
import { PERMISSION_ID, SYSTEM_ROLE_ID } from '@fry-it/risr-core';
import { OrganisationService } from '@fry-it/risr-core';
import { setPageTitle } from '@fry-it/risr-core';
import { isOKResponse, Response } from '@fry-it/risr-core';
import { OidcService } from '@fry-it/risr-core';
import { RouterLink } from '@angular/router';

@Component({
    selector: 'eas-credentials',
    templateUrl: './list-credentials.component.html',
    imports: [
    RouterLink,
    MatTabGroup,
    MatTab
],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [
    ]
})
export class ListCredentialsComponent {
  private oidc = inject(OidcService);

  user = input<string>();
  remoteUser = computed(() => this.user());

  public isForRemoteUser = computed(() => this.remoteUser() !== undefined);
  public accountId = computed(() => this.remoteUser() || this.authService.currentUser());
  private isAdmin = signal<boolean>(false);
  private isSuperAdmin = signal<boolean>(false);
  protected canAccessAPIKeys = computed(() => this.isAdmin());
  private hasManagePermission = signal<boolean>(false);

  allowedAccountTypes: CredentialTypes[] = [];
  private enabledMFAs = signal<MFAMethod[]>([]);
  loginCredentials = signal<(LocalCredential | ProxyCredential)[]>([]);
  apiCredentials = signal<ApiCredential[]>([]);
  readableName = READABLE_CREDENTIAL_TYPES;

  newlyCreatedAPICredential = signal<ApiCredential | null>(null);

  protected readonly CredentialTypes = CredentialTypes;

  constructor(
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private authService: AuthService,
    private organisationService: OrganisationService,
    private securityService: SecurityService,
    private credentialService: CredentialService,
  ) {
    this.setPermissions();
  }

  ngOnInit() {
    setPageTitle('Account security');
    this.loadCredentials();
    this.setOrganisationAllowedAccountTypes();
  }

  private async loadCredentials() {
    const response = await this.credentialService.getCredentials(this.accountId())
    if (!isOKResponse(response)) {
      return;
    }

    const credentials = response.data;
    this.loginCredentials.set(credentials.filter(cred => cred.type !== CredentialTypes.API) as (LocalCredential | ProxyCredential)[]);
    this.apiCredentials.set(credentials.filter(cred => cred.type === CredentialTypes.API) as ApiCredential[]);
  }

  private async setOrganisationAllowedAccountTypes() {
    const currentOrganisation = this.organisationService.currentOrganisation();
    if (currentOrganisation) {
      this.allowedAccountTypes = currentOrganisation.allowedAccountTypes;
      this.enabledMFAs.set(currentOrganisation.enabledMFAs);
    }
  }

  // Permissions
  private async setPermissions() {
    this.isSuperAdmin.set(await this.securityService.hasRole(SYSTEM_ROLE_ID.SUPER_ADMIN));
    this.isAdmin.set(await this.securityService.hasRole(SYSTEM_ROLE_ID.ADMIN));

    let permission = String(PERMISSION_ID.CREDENTIAL_MANAGE);
    if (!this.isForRemoteUser()) {
      permission += '.own';
    }
    this.hasManagePermission.set(await this.securityService.hasPermission(permission));
  }

  canCreateCredential(type: CredentialTypes): boolean {
    if (type == CredentialTypes.PROXY) {
      return this.isAdmin();
    }

    if (type === CredentialTypes.API) {
      return this.isSuperAdmin();
    }

    return this.allowedAccountTypes.includes(type) && this.hasManagePermission();
  }

  canAccessTwoFactorAuth(type: CredentialTypes.LOCAL | CredentialTypes.PROXY) {
    return type === CredentialTypes.LOCAL && this.enabledMFAs().length > 1;
  }

  canUpdateUsername(type: CredentialTypes): boolean {
    return type === CredentialTypes.LOCAL && this.hasManagePermission();
  }

  canUpdateSSOID(type: CredentialTypes): boolean {
    return type === CredentialTypes.PROXY && this.isAdmin();
  }

  canSendPasswordEmail(type: CredentialTypes): boolean {
    return type === CredentialTypes.LOCAL && this.hasManagePermission() && this.isForRemoteUser();
  }

  canUpdatePassword(type: CredentialTypes): boolean {
    return type === CredentialTypes.LOCAL && (!this.isForRemoteUser() || this.hasManagePermission());
  }

  canDelete(): boolean {
    if (this.isForRemoteUser()) {
      return this.hasManagePermission();
    } else {
      return this.hasManagePermission() && this.loginCredentials().length > 1
    }
  }

  // Actions
  async addLoginCredential(type: CredentialTypes) {
    await this.oidc.ensureStepup();

    if (type === CredentialTypes.LOCAL) {
      this.createLocalCredential();
    } else if (type === CredentialTypes.PROXY) {
      this.createProxyCredential();
    }
  }

  private createLocalCredential() {
    const dialogRef = this.dialog.open(CreateLocalCredentialDialogComponent, {
      minWidth: '20vw',
      disableClose: true,
      data: { isForRemoteUser: this.isForRemoteUser() }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        const org = this.authService.currentOrganisation();
        if (!org) {
          throw new Error('Organisation not found');
        }
        const localCredential: LocalCredential = {
          type: CredentialTypes.LOCAL,
          organisationId: org,
          username: result.username,
          password: result.password
        }
        const response = await this.credentialService.createCredential(localCredential, this.accountId());
        this.handleResponse(response, $localize`Local credential created successfully!`, $localize`Failed to create Local credential`);
        this.loadCredentials();
      }
    });
  }

  private createProxyCredential() {
    const dialogRef = this.dialog.open(CreateProxyCredentialDialogComponent, {
      minWidth: '20vw',
      disableClose: true,
      data: { isForRemoteUser: this.isForRemoteUser() }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        const org = this.authService.currentOrganisation();
        if (!org) {
          throw new Error('Organisation not found');
        }
        const proxyCredential: ProxyCredential = {
          type: CredentialTypes.PROXY,
          organisationId: org,
          username: result.username
        }
        const response = await this.credentialService.createCredential(proxyCredential, this.accountId());
        this.handleResponse(response, $localize`SSO credential created successfully!`, $localize`Failed to create SSO credential`);
        this.loadCredentials();
      }
    });
  }

  async createApiCredential() {
    await this.oidc.ensureStepup();
    const apiCredential: ApiCredential = { type: CredentialTypes.API };
    const response = await this.credentialService.createCredential(apiCredential, this.accountId());
    this.handleResponse(response, $localize`API credential created successfully!`, $localize`Failed to create API credential`);
    this.loadCredentials();
    this.newlyCreatedAPICredential.set(response.data as ApiCredential);
  }

  copyToClipboard() {
    if (this.newlyCreatedAPICredential()) {
      const text = `public_key: ${this.newlyCreatedAPICredential()?.public_key}\nsecret_key: ${this.newlyCreatedAPICredential()?.secret_key}`;
      navigator.clipboard.writeText(text).then(() => {
        this.snackBar.open($localize`API credentials copied to clipboard!`, $localize`Close`, { duration: 3000 });
      });
    }
  }

  async updateUsername(credential: LocalCredential | ProxyCredential) {
    await this.oidc.ensureStepup();
    const dialogRef = this.dialog.open(UpdateCredentialUsernameDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { currentCredentialUsername: credential.username, credentialType: credential.type }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        const currentCredentialUsername = credential.username;
        if (!currentCredentialUsername) {
          throw new Error('Username not found');
        }
        const response = await this.credentialService.updateCredentialUsername(
          credential.type,
          currentCredentialUsername,
          result.newUsername,
          this.accountId()
        );
        this.handleResponse(response, $localize`Username updated successfully!`, $localize`Failed to update username`);
        this.loadCredentials();
      }
    });
  }

  async updatePassword(localCredential: LocalCredential) {
    await this.oidc.ensureStepup();
    const dialogRef = this.dialog.open(UpdatePasswordDialogComponent, {
      width: '400px',
      disableClose: true,
      data: { username: localCredential.username }
    });

    const accountId = this.accountId();
    if (!accountId) {
      throw new Error('Account ID not defined');
    }

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        const { newPassword, passwordConfirmation } = result;
        if (!localCredential.username) {
          throw new Error('Username not defined');
        }
        const response = await this.credentialService.updatePassword(localCredential.username, newPassword, passwordConfirmation, accountId);
        this.handleResponse(response, $localize`Password updated successfully!`, $localize`Failed to update password`);
      }
    });
  }

  async deleteCredential(credential: LocalCredential | ProxyCredential | ApiCredential) {
    await this.oidc.ensureStepup();
    const confirmation = await Swal.fire({
      title: $localize`Are you sure?`,
      text: $localize`Are you sure you want to delete this credential? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, delete it!`,
      cancelButtonText: $localize`No, keep it`
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    var response;
    if (credential.type === CredentialTypes.API) {
      if (!credential.public_key) {
        throw new Error('Public key not defined');
      }
      response = await this.credentialService.deleteApiCredential(credential.public_key, this.accountId());
    } else {
      response = await this.credentialService.deleteLoginCredential(credential, this.accountId());
    }

    this.handleResponse(response, $localize`Credential deleted successfully!`, $localize`Failed to delete credential`);
    this.newlyCreatedAPICredential.set(null);
    this.loadCredentials();
  }

  async sendPasswordEmail(localCredential: LocalCredential) {
    await this.oidc.ensureStepup();
    const confirmation = await Swal.fire({
      title: $localize`Are you sure?`,
      text: $localize`This will send an email to this user containing a link for them to follow to reset their password. Would you like to continue?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: $localize`Yes, send it!`,
      cancelButtonText: $localize`No, cancel`
    });

    if (!confirmation.isConfirmed) {
      return;
    }
    if (!localCredential.username) {
      throw new Error('Username not defined');
    }
    const accountId = this.accountId();
    if (!accountId) {
      throw new Error('Account ID not defined');
    }
    const response = await this.credentialService.sendPasswordResetEmail(
      accountId,
      localCredential.username
    );

    this.handleResponse(response, $localize`An email would be sent shortly!`, $localize`Failed to reset the password`);
  }

  // Aria label helpers
  twoFactorAuthLabel(username?: string) {
    return $localize`Two factor authentication for ${username}`;
  }

  editUsernameLabel(username?: string) {
    return $localize`Edit username ${username}`;
  }

  editSSOIDLabel(username?: string) {
    return $localize`Edit SSO ID ${username}`;
  }

  sendPasswordEmailLabel(username?: string) {
    return $localize`Send password reset email for ${username}`;
  }

  changePasswordLabel(username?: string) {
    return $localize`Change password for ${username}`;
  }

  removeLabel(identifier?: string) {
    return $localize`Remove ${identifier}`;
  }

  handleResponse(response: Response<any>, successMessage: string, errorMessage: string) {
    if (response.state !== "ok") {
      const suffix = $localize`Please try again later.`;
      this.snackBar.open(`${errorMessage}: ${response.error}. ${suffix}`, $localize`Close`, {
        duration: 3000,
      });
      return;
    }

    this.snackBar.open(successMessage, $localize`Close`, { duration: 3000 });
  }
}
